import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import ContextualBreadcrumb from '../../components/ui/ContextualBreadcrumb';
import FloatingActionButton from '../../components/ui/FloatingActionButton';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import AIPhaseManagement from '../phase-management/components/AIPhaseManagement';

const WorkersManagePhase = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();

  const phaseId = searchParams?.get('phaseId') || location?.state?.phaseId;
  const projectId = searchParams?.get('projectId') || location?.state?.projectId;

  const [project, setProject] = useState(null);
  const [phases, setPhases] = useState([]);
  const [projectName, setProjectName] = useState(location?.state?.projectName || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading && user) {
      loadWorkerPhase();
    } else if (!authLoading && !user) {
      setError('Please log in to manage this phase');
      setLoading(false);
    }
  }, [authLoading, user, phaseId, projectId]);

  const loadWorkerPhase = async () => {
    try {
      setLoading(true);
      setError('');

      if (!projectId || !phaseId) {
        setError('Missing project or phase details');
        return;
      }

      const { data: projectData, error: projectError } = await supabase
        ?.from('projects')
        ?.select('*')
        ?.eq('id', projectId)
        ?.single();

      if (projectError) throw projectError;

      const { data: phaseData, error: phaseError } = await supabase
        ?.from('phases')
        ?.select('*')
        ?.eq('id', phaseId)
        ?.single();

      if (phaseError) throw phaseError;

      setProject(projectData);
      setProjectName(projectData?.name || location?.state?.projectName || '');
      setPhases(phaseData ? [phaseData] : []);
    } catch (err) {
      console.error('Error loading worker phase:', err);
      setError(err?.message || 'Failed to load phase');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/worker-home-page');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <PrimaryNavigation />
        <ContextualBreadcrumb />

        <div className="main-content-mobile-nav flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <PrimaryNavigation />
        <ContextualBreadcrumb />

        <div className="main-content-mobile-nav max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            type="button"
            onClick={handleBack}
            className="mb-6 text-sm text-gray-600 hover:text-gray-900"
          >
            ← Back to My Projects
          </button>

          <div className="bg-white border border-red-200 rounded-xl p-6 text-center">
            <p className="text-red-600 mb-4">{error}</p>

            <button
              type="button"
              onClick={loadWorkerPhase}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              Retry
            </button>
          </div>
        </div>

        <FloatingActionButton />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PrimaryNavigation />
      <ContextualBreadcrumb />

      <div className="main-content-mobile-nav max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AIPhaseManagement
          project={project}
          phases={phases}
          projectName={projectName}
          onBack={handleBack}
          userRole="worker"
          currentUserId={user?.id}
          initialPhases={phases}
        />
      </div>

      <FloatingActionButton />
    </div>
  );
};

export default WorkersManagePhase;
